import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';
import { PayslipDialog } from './PayslipDialog';
import { TimesheetDrilldown } from './TimesheetDrilldown';

/**
 * PayrollCenter — Centralized payroll command center for org admins.
 *
 * Lives as a sub-tab inside the Financial tab in AdminDashboard. Provides:
 *
 *   1. Period selector — Current month / Last month / Custom range
 *   2. Summary cards — staff count, hours, projected gross, generated/missing
 *   3. Staff payroll table — per-staff hours, hourly rate, payment-details
 *      readiness, last payslip status, per-row actions (Generate / Open / Email)
 *   4. Bulk-generate dialog — runs in one click for all staff in the period
 *
 * Reuses the existing PayslipDialog (per-staff generate/open/email) so we
 * don't duplicate the per-staff logic.
 */

const ROLE_LABEL = {
  staff: 'Staff',
  driver: 'Driver',
  room_leader: 'Room Leader',
  deputy_manager: 'Deputy',
  org_admin: 'Admin',
  org_owner: 'Owner',
  parent: 'Parent',
};

const EMP_TYPE_TONE = {
  employee:   { bg: 'hsl(190 65% 92%)', fg: 'hsl(190 65% 25%)', label: '👨‍🏫 Employee' },
  contractor: { bg: 'hsl(45 95% 90%)',  fg: 'hsl(35 80% 30%)',  label: '🧑‍💼 Contractor' },
  volunteer:  { bg: 'hsl(140 50% 90%)', fg: 'hsl(140 55% 28%)', label: '🤝 Volunteer' },
};

const STATUS_TONE = {
  draft: { bg: 'hsl(45 95% 92%)', fg: 'hsl(35 80% 30%)', label: 'Draft' },
  sent:  { bg: 'hsl(220 70% 92%)', fg: 'hsl(220 60% 30%)', label: 'Sent' },
  paid:  { bg: 'hsl(140 70% 92%)', fg: 'hsl(140 55% 26%)', label: 'Paid' },
};

function isoDate(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
    .toISOString().slice(0, 10);
}

function getMonthRange(offsetMonths = 0) {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth() + offsetMonths, 1);
  const last = new Date(now.getFullYear(), now.getMonth() + offsetMonths + 1, 0);
  return { start: isoDate(first), end: isoDate(last) };
}

function money(n) {
  if (n == null || Number.isNaN(Number(n))) return '£0.00';
  return `£${Number(n).toFixed(2)}`;
}

export function PayrollCenter() {
  // --- Period selection ---------------------------------------------------
  const initial = useMemo(() => getMonthRange(0), []);
  const [periodPreset, setPeriodPreset] = useState('this_month');
  const [startDate, setStartDate] = useState(initial.start);
  const [endDate, setEndDate] = useState(initial.end);
  const [includeArchived, setIncludeArchived] = useState(false);

  // --- Data ---------------------------------------------------------------
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(() => new Set());

  // --- Per-staff PayslipDialog --------------------------------------------
  // Carries the parent's period + row-level hours through to the
  // dialog so admins don't have to click "🔄 Pull" — Task 3.
  const [psDialog, setPsDialog] = useState({
    open: false, staffId: null, staffName: '', rate: 0,
    employmentType: 'employee',
    prefillHours: 0, prefillPeriodStart: null, prefillPeriodEnd: null,
  });

  // --- Timesheet drill-down popover ---------------------------------------
  // Opens when an admin clicks the "Xh" hours cell — surfaces the exact
  // clock-in/out pairs that were summed into the row total so payroll
  // discrepancies (missing clock-outs, break drift, etc.) are easy to
  // spot before generating a payslip.
  const [drillDialog, setDrillDialog] = useState({
    open: false, staffId: null, staffName: '', totalHours: 0,
  });

  // --- Bulk-generate flow -------------------------------------------------
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkOptions, setBulkOptions] = useState({
    include_zero_hours: false,
    skip_if_exists: true,
  });

  // --- Effects ------------------------------------------------------------
  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api.getPayrollPeriodSummary({
        start_date: startDate,
        end_date: endDate,
        include_archived: includeArchived,
      });
      setSummary(data);
      // Reset selection to keep ids in sync with currently-visible rows
      setSelected(new Set());
    } catch (err) {
      setError(err.message || 'Failed to load payroll data');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, includeArchived]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  // --- Helpers ------------------------------------------------------------
  function applyPreset(preset) {
    setPeriodPreset(preset);
    if (preset === 'this_month') {
      const r = getMonthRange(0); setStartDate(r.start); setEndDate(r.end);
    } else if (preset === 'last_month') {
      const r = getMonthRange(-1); setStartDate(r.start); setEndDate(r.end);
    }
    // 'custom' leaves the current dates untouched
  }

  function toggleSelect(staffId) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(staffId)) next.delete(staffId); else next.add(staffId);
      return next;
    });
  }
  function toggleSelectAll() {
    if (!summary?.rows) return;
    setSelected((prev) => {
      if (prev.size === summary.rows.length) return new Set();
      return new Set(summary.rows.map((r) => r.staff_id));
    });
  }

  async function runBulkGenerate() {
    const ids = selected.size ? Array.from(selected) : null; // null = all
    if (!ids && (summary?.rows?.length || 0) > 25) {
      const ok = window.confirm(
        `Generate draft payslips for ALL ${summary.rows.length} staff in this period?\n\n` +
        `Tick rows in the table first if you want to limit the run.`,
      );
      if (!ok) return;
    }
    setBulkRunning(true);
    setBulkResult(null);
    try {
      const res = await api.bulkGeneratePayslips({
        start_date: startDate,
        end_date: endDate,
        staff_ids: ids,
        include_zero_hours: bulkOptions.include_zero_hours,
        skip_if_exists: bulkOptions.skip_if_exists,
      });
      setBulkResult(res);
      const s = res.summary || {};
      if (s.generated > 0) {
        toast.success(`Generated ${s.generated} draft payslip${s.generated === 1 ? '' : 's'}`, {
          description: `Skipped ${s.skipped || 0}, failed ${s.failed || 0}. Open a row to email or mark paid.`,
        });
      } else if (s.skipped > 0 && s.failed === 0) {
        toast.info('No new payslips generated', { description: `${s.skipped} staff were skipped (already exist or 0 hours).` });
      } else {
        toast.warning('Bulk generation completed with issues', { description: `Generated ${s.generated || 0}, failed ${s.failed || 0}.` });
      }
      await fetchSummary();
    } catch (err) {
      toast.error(err.message || 'Bulk generation failed');
    } finally {
      setBulkRunning(false);
    }
  }

  // --- Rendering ----------------------------------------------------------
  const totals = summary?.totals || {};
  const rows = summary?.rows || [];
  const allSelected = rows.length > 0 && selected.size === rows.length;

  return (
    <div data-testid="payroll-center" style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {/* --- Header / period selector ----------------------------------- */}
      <div className="card" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'flex-end' }} data-testid="payroll-header">
        <div style={{ flex: '1 1 auto', minWidth: 220 }}>
          <h2 style={{ margin: '0 0 0.15rem 0' }}>💷 Payroll Center</h2>
          <p style={{ margin: 0, fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))' }}>
            Run payroll for the whole organisation from one screen. Hours are pulled live from timesheets;
            payslips are drafts you can review and email per staff member.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          {[
            { v: 'this_month', label: 'This month' },
            { v: 'last_month', label: 'Last month' },
            { v: 'custom',     label: 'Custom' },
          ].map((opt) => (
            <button
              key={opt.v}
              type="button"
              onClick={() => applyPreset(opt.v)}
              className={periodPreset === opt.v ? 'btn btn-primary' : 'btn btn-ghost'}
              style={{ padding: '0.4rem 0.7rem', fontSize: '0.85rem' }}
              data-testid={`payroll-preset-${opt.v}`}
            >
              {opt.label}
            </button>
          ))}

          <label style={smallLabelStyle}>
            <span>Start</span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPeriodPreset('custom'); }}
              data-testid="payroll-start-date"
              style={dateInputStyle}
            />
          </label>
          <label style={smallLabelStyle}>
            <span>End</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPeriodPreset('custom'); }}
              data-testid="payroll-end-date"
              style={dateInputStyle}
            />
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem' }}>
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
              data-testid="payroll-include-archived"
            />
            <span>Include archived</span>
          </label>

          <button
            type="button"
            className="btn btn-ghost"
            onClick={fetchSummary}
            disabled={loading}
            style={{ fontSize: '0.85rem' }}
            data-testid="payroll-refresh"
          >
            {loading ? '…' : '↻ Refresh'}
          </button>
        </div>
      </div>

      {/* --- Summary cards ---------------------------------------------- */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '0.75rem',
        }}
        data-testid="payroll-summary-cards"
      >
        <Stat label="Staff in run" value={totals.staff_count ?? 0} />
        <Stat label="Total hours" value={(totals.total_hours ?? 0).toFixed(2)} suffix="h" />
        <Stat label="Projected gross" value={money(totals.projected_gross_total)} accent="hsl(220 70% 35%)" />
        <Stat label="Payslips this period" value={`${totals.payslips_existing ?? 0} / ${rows.length}`} hint={`${totals.payslips_missing ?? 0} missing`} />
        <Stat label="Net (issued)" value={money(totals.payslips_net_total)} accent="hsl(140 55% 26%)" />
      </div>

      {/* --- Bulk-action bar -------------------------------------------- */}
      <div
        className="card"
        style={{
          display: 'flex',
          gap: '0.75rem',
          alignItems: 'center',
          flexWrap: 'wrap',
          background: 'hsl(var(--primary) / 0.04)',
        }}
        data-testid="payroll-bulk-bar"
      >
        <strong style={{ fontSize: '0.9rem' }}>
          {selected.size > 0
            ? `${selected.size} of ${rows.length} selected`
            : `Generate for ${rows.length} staff`}
        </strong>

        <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.82rem' }}>
          <input
            type="checkbox"
            checked={bulkOptions.skip_if_exists}
            onChange={(e) => setBulkOptions({ ...bulkOptions, skip_if_exists: e.target.checked })}
            data-testid="payroll-skip-if-exists"
          />
          <span>Skip if payslip already exists</span>
        </label>

        <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.82rem' }}>
          <input
            type="checkbox"
            checked={bulkOptions.include_zero_hours}
            onChange={(e) => setBulkOptions({ ...bulkOptions, include_zero_hours: e.target.checked })}
            data-testid="payroll-include-zero-hours"
          />
          <span>Include staff with 0 hours</span>
        </label>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
          <button
            type="button"
            className="btn btn-primary"
            onClick={runBulkGenerate}
            disabled={bulkRunning || loading || rows.length === 0}
            data-testid="payroll-bulk-generate"
            style={{ padding: '0.5rem 0.95rem' }}
          >
            {bulkRunning ? 'Generating…' : `🚀 Generate ${selected.size > 0 ? 'selected' : 'all'}`}
          </button>
        </div>
      </div>

      {/* --- Last bulk-run summary (transient feedback) ---------------- */}
      {bulkResult && (
        <div className="card" data-testid="payroll-bulk-result" style={{ borderLeft: '4px solid hsl(var(--primary))' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
            <strong>Last bulk run — {bulkResult.period_start} → {bulkResult.period_end}</strong>
            <button type="button" className="btn btn-ghost" onClick={() => setBulkResult(null)} style={{ fontSize: '0.78rem' }} aria-label="Dismiss">
              ✕
            </button>
          </div>
          <div style={{ marginTop: '0.5rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.5rem' }}>
            <MiniStat label="Generated" value={bulkResult.summary.generated} tone="success" />
            <MiniStat label="Skipped" value={bulkResult.summary.skipped} tone="warning" />
            <MiniStat label="Failed" value={bulkResult.summary.failed} tone={bulkResult.summary.failed > 0 ? 'error' : 'neutral'} />
          </div>
          {bulkResult.failed?.length > 0 && (
            <details style={{ marginTop: '0.5rem' }}>
              <summary style={{ cursor: 'pointer', fontSize: '0.85rem', color: 'hsl(0 60% 40%)' }}>View failures ({bulkResult.failed.length})</summary>
              <ul style={{ marginTop: '0.4rem', fontSize: '0.82rem' }}>
                {bulkResult.failed.map((f, i) => (
                  <li key={i}>{f.staff_id}: {f.reason}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {/* --- Staff table ------------------------------------------------ */}
      {loading ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: 'hsl(var(--muted-foreground))' }}>
          Loading payroll data…
        </div>
      ) : error ? (
        <div className="card" style={{ borderLeft: '4px solid hsl(0 60% 50%)', color: 'hsl(0 60% 40%)' }} data-testid="payroll-error">
          {error}
        </div>
      ) : rows.length === 0 ? (
        <div className="empty-state" style={{ padding: '2rem' }} data-testid="payroll-empty">
          <p>No staff to process for this period.</p>
          <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.85rem' }}>
            Try widening the date range or toggling "Include archived".
          </p>
        </div>
      ) : (
        <div className="card" style={{ overflowX: 'auto' }} data-testid="payroll-table-wrap">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }} data-testid="payroll-table">
            <thead>
              <tr style={{ borderBottom: '2px solid hsl(var(--border))' }}>
                <th style={th}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    data-testid="payroll-select-all"
                    aria-label="Select all"
                  />
                </th>
                <th style={th}>Staff</th>
                <th style={th}>Type</th>
                <th style={{ ...th, textAlign: 'right' }}>Hours</th>
                <th style={{ ...th, textAlign: 'right' }}>Rate</th>
                <th style={{ ...th, textAlign: 'right' }}>Projected</th>
                <th style={th}>Payment details</th>
                <th style={th}>Last payslip</th>
                <th style={{ ...th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const et = EMP_TYPE_TONE[r.employment_type] || EMP_TYPE_TONE.employee;
                const statusTone = r.last_payslip_status ? STATUS_TONE[r.last_payslip_status] : null;
                const checked = selected.has(r.staff_id);
                return (
                  <tr
                    key={r.staff_id}
                    data-testid={`payroll-row-${r.staff_id}`}
                    style={{
                      borderTop: '1px solid hsl(var(--border))',
                      opacity: r.is_active === false ? 0.6 : 1,
                      background: checked ? 'hsl(var(--primary) / 0.06)' : undefined,
                    }}
                  >
                    <td style={td}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleSelect(r.staff_id)}
                        data-testid={`payroll-select-${r.staff_id}`}
                        aria-label={`Select ${r.name}`}
                      />
                    </td>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{r.name}</div>
                      <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                        {ROLE_LABEL[r.role] || r.role || '—'} · {r.pay_frequency}
                      </div>
                    </td>
                    <td style={td}>
                      <span className="badge" style={{ background: et.bg, color: et.fg, fontSize: '0.72rem', padding: '0.15rem 0.45rem', borderRadius: 999, fontWeight: 600 }}>
                        {et.label}
                      </span>
                    </td>
                    <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => setDrillDialog({
                          open: true,
                          staffId: r.staff_id,
                          staffName: r.name,
                          totalHours: r.hours_worked,
                        })}
                        title="Click to see the clock-in / clock-out pairs behind this total"
                        data-testid={`payroll-hours-drilldown-${r.staff_id}`}
                        style={{
                          background: 'transparent',
                          border: 0,
                          padding: 0,
                          margin: 0,
                          textDecoration: 'underline dotted',
                          textUnderlineOffset: '2px',
                          cursor: 'pointer',
                          color: 'inherit',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        <span style={{ fontWeight: r.hours_worked > 0 ? 600 : 400, color: r.hours_worked > 0 ? 'inherit' : 'hsl(var(--muted-foreground))' }}>
                          {r.hours_worked.toFixed(2)}h
                        </span>
                      </button>
                      {r.overtime_days > 0 && (
                        <div style={{ fontSize: '0.7rem', color: 'hsl(35 80% 35%)' }}>
                          OT: {r.overtime_days}d
                        </div>
                      )}
                    </td>
                    <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{money(r.hourly_rate)}</td>
                    <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{money(r.projected_gross)}</td>
                    <td style={td}>
                      {r.has_payment_details ? (
                        <span className="badge" style={{ background: 'hsl(140 70% 92%)', color: 'hsl(140 55% 26%)', fontSize: '0.72rem' }}>✓ Ready</span>
                      ) : (
                        <span className="badge" style={{ background: 'hsl(0 75% 94%)', color: 'hsl(0 60% 35%)', fontSize: '0.72rem' }} title="Missing bank account or sort code">⚠ Missing</span>
                      )}
                    </td>
                    <td style={td}>
                      {r.last_payslip_id ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
                          <span
                            className="badge"
                            style={{
                              background: statusTone?.bg || 'hsl(220 15% 92%)',
                              color: statusTone?.fg || 'hsl(220 15% 35%)',
                              fontSize: '0.72rem',
                              alignSelf: 'flex-start',
                              padding: '0.12rem 0.45rem',
                              borderRadius: 999,
                              fontWeight: 600,
                            }}
                          >
                            {statusTone?.label || r.last_payslip_status}
                          </span>
                          <span style={{ fontSize: '0.72rem', color: 'hsl(var(--muted-foreground))' }}>
                            {r.last_payslip_net != null ? money(r.last_payslip_net) : ''}
                          </span>
                        </div>
                      ) : (
                        <span style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.78rem' }}>—</span>
                      )}
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => setPsDialog({
                          open: true,
                          staffId: r.staff_id,
                          staffName: r.name,
                          rate: r.hourly_rate,
                          employmentType: r.employment_type || 'employee',
                          // Task 3 — pre-fill from the summary row so
                          // the dialog opens with hours already set.
                          prefillHours: r.hours_worked || 0,
                          prefillPeriodStart: startDate,
                          prefillPeriodEnd: endDate,
                        })}
                        style={{ fontSize: '0.78rem', padding: '0.25rem 0.6rem' }}
                        data-testid={`payroll-open-${r.staff_id}`}
                        title={r.last_payslip_id ? 'View / regenerate' : 'Generate payslip'}
                      >
                        {r.last_payslip_id ? '📄 View' : '✨ Generate'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* --- Per-staff payslip dialog reuse ---------------------------- */}
      {psDialog.open && (
        <PayslipDialog
          open={psDialog.open}
          onClose={() => {
            setPsDialog({
              open: false, staffId: null, staffName: '', rate: 0,
              employmentType: 'employee',
              prefillHours: 0, prefillPeriodStart: null, prefillPeriodEnd: null,
            });
            fetchSummary();
          }}
          staffId={psDialog.staffId}
          staffName={psDialog.staffName}
          defaultRate={psDialog.rate}
          employmentType={psDialog.employmentType}
          prefillHours={psDialog.prefillHours}
          prefillPeriodStart={psDialog.prefillPeriodStart}
          prefillPeriodEnd={psDialog.prefillPeriodEnd}
        />
      )}

      {/* Timesheet drill-down popover — opens on clicking the "Xh" cell.
          Uses the same period the payroll table is already viewing so
          the shifts shown here reconcile exactly to the row total. */}
      <TimesheetDrilldown
        open={drillDialog.open}
        onClose={() => setDrillDialog({ open: false, staffId: null, staffName: '', totalHours: 0 })}
        staffId={drillDialog.staffId}
        staffName={drillDialog.staffName}
        startDate={startDate}
        endDate={endDate}
        totalHours={drillDialog.totalHours}
      />
    </div>
  );
}

// --- Sub-components -------------------------------------------------------

function Stat({ label, value, suffix, accent, hint }) {
  return (
    <div className="card" style={{ padding: '0.85rem 1rem' }} data-testid={`payroll-stat-${(label || '').toLowerCase().replace(/\s+/g, '-')}`}>
      <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: 'hsl(var(--muted-foreground))', letterSpacing: '0.05em' }}>
        {label}
      </div>
      <div style={{ fontSize: '1.4rem', fontWeight: 700, marginTop: '0.2rem', color: accent }}>
        {value}{suffix ? <span style={{ fontSize: '0.85rem', fontWeight: 500, marginLeft: '0.1rem' }}>{suffix}</span> : null}
      </div>
      {hint && (
        <div style={{ fontSize: '0.72rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.15rem' }}>{hint}</div>
      )}
    </div>
  );
}

function MiniStat({ label, value, tone }) {
  const toneMap = {
    success: { bg: 'hsl(140 70% 92%)', fg: 'hsl(140 55% 26%)' },
    warning: { bg: 'hsl(45 95% 92%)', fg: 'hsl(35 80% 30%)' },
    error:   { bg: 'hsl(0 75% 94%)',  fg: 'hsl(0 60% 35%)' },
    neutral: { bg: 'hsl(220 15% 92%)', fg: 'hsl(220 15% 35%)' },
  };
  const t = toneMap[tone] || toneMap.neutral;
  return (
    <div style={{ padding: '0.5rem 0.75rem', borderRadius: 'var(--radius)', background: t.bg, color: t.fg }}>
      <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', opacity: 0.85 }}>{label}</div>
      <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>{value ?? 0}</div>
    </div>
  );
}

// --- Styles ---------------------------------------------------------------

const th = { textAlign: 'left', padding: '0.6rem 0.5rem', fontWeight: 600, fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.04em' };
const td = { padding: '0.65rem 0.5rem', verticalAlign: 'top' };

const smallLabelStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.15rem',
  fontSize: '0.72rem',
  color: 'hsl(var(--muted-foreground))',
  textTransform: 'uppercase',
};

const dateInputStyle = {
  padding: '0.4rem 0.55rem',
  border: '1px solid hsl(var(--border))',
  borderRadius: 'var(--radius)',
  fontSize: '0.85rem',
  background: '#fff',
};

export default PayrollCenter;
